import type { JSX, ReactNode } from 'react';

/**
 * 160: render stored legal HTML as elements, never as markup — the admin's preview.
 *
 * 🔴 Deliberately a MIRROR of `app/src/components/ui/RichText.tsx` rather than an import: the two
 * packages have no shared module and no npm workspaces (CLAUDE.md, Architecture). The allowlist and
 * the href rule are restated here, and `LegalDocumentsCard.test.tsx` pins that the same input
 * produces the same element shape on both surfaces — because a preview that renders differently
 * from the customer page is worse than having no preview at all (US1 AC3).
 */

const BLOCK_ELEMENTS = new Set(['h2', 'h3', 'p', 'ul', 'ol', 'li']);
const INLINE_ELEMENTS = new Set(['strong', 'em', 'a', 'br']);
const CONTENT_DROPPED = new Set(['script', 'style']);
const SAFE_SCHEMES = ['http:', 'https:', 'mailto:'];

const CLASS_NAMES: Record<string, string> = {
  h2: 'text-base font-semibold text-ink mt-4 mb-2',
  h3: 'text-sm font-semibold text-ink mt-3 mb-2',
  p: 'text-sm text-steel leading-relaxed mb-2',
  ul: 'list-disc pl-5 mb-2 flex flex-col gap-1',
  ol: 'list-decimal pl-5 mb-2 flex flex-col gap-1',
  li: 'text-sm text-steel leading-relaxed',
  a: 'text-blue-700 underline',
};

function safeHref(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const normalised = [...raw]
    .filter((character) => (character.codePointAt(0) ?? 0) > 0x20)
    .join('')
    .toLowerCase();
  if (SAFE_SCHEMES.some((scheme) => normalised.startsWith(scheme))) return raw.trim();
  return undefined;
}

function renderNode(node: Node, key: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  const tag = element.tagName.toLowerCase();

  if (CONTENT_DROPPED.has(tag)) return null;

  const children = renderChildren(element, key);

  if (tag === 'br') return <br key={key} />;

  if (tag === 'a') {
    const href = safeHref(element.getAttribute('href'));
    if (!href) return <span key={key}>{children}</span>;
    return (
      <a key={key} href={href} className={CLASS_NAMES.a} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  if (BLOCK_ELEMENTS.has(tag) || INLINE_ELEMENTS.has(tag)) {
    const Tag = tag as keyof JSX.IntrinsicElements;
    return (
      <Tag key={key} className={CLASS_NAMES[tag]}>
        {children}
      </Tag>
    );
  }

  return <span key={key}>{children}</span>;
}

function renderChildren(parent: Node, keyPrefix: string): ReactNode[] {
  return Array.from(parent.childNodes).map((child, index) =>
    renderNode(child, `${keyPrefix}-${index}`),
  );
}

export interface RichTextProps {
  html: string;
  className?: string;
}

export function RichText({ html, className = '' }: RichTextProps) {
  const parsed = new DOMParser().parseFromString(html ?? '', 'text/html');
  return <div className={className}>{renderChildren(parsed.body, 'rt')}</div>;
}
